import {useState} from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {StudySearchItem} from 'studies/StudySearchInput';

import {isSeedDataConfigValid} from './SeedDataRender';
import {SeedDataConfig} from './SeedDataTypes';

function isOnlyWhiteSpace(str: string): boolean {
  return str.trim().length === 0;
}

function getCreateAnalysisRequestParamsInfo({
  analysisName,
  folderId,
  analysisTableName,
  studyItems,
  seedDataConfig,
}: CreateAnalysisState): CreateAnalysisRequestParamsInfo {
  if (
    isOnlyWhiteSpace(analysisName) ||
    folderId == null ||
    (analysisTableName != null && isOnlyWhiteSpace(analysisTableName)) ||
    (seedDataConfig != null && !isSeedDataConfigValid(seedDataConfig))
  ) {
    return {isComplete: false};
  }
  return {
    isComplete: true,
    requestParams: {
      name: analysisName,
      folderId,
      studyIds: studyItems.map((item) => item.id),
      seedDataConfig,
    },
    analysisTableName: analysisTableName ?? null,
  };
}

/**
 * State for configuring how to create a new Analysis.
 *
 * `CreateAnalysisState` is validated and translated to the request params for `AnalysisDAO.createAnalysis`
 * by `getCreateAnalysisRequestParamsInfo`.
 */
export interface CreateAnalysisState {
  /**
   * Name of the new analysis.
   *
   * A non-empty name is required; an empty string means the user hasn't entered a valid name.
   */
  analysisName: string;
  /**
   * Folder to place the analysis.
   *
   * A folder is required; null means the user hasn't selected a valid folder.
   */
  folderId: string | null;
  /**
   * The name of a table to add to the newly created analysis.
   *
   * This is used by callers that add a table to the analysis in a separate web request, after the analysis
   * has been created.
   *
   * An undefined value means a table name is not required, and the table name field will not be shown.
   *
   * An empty string means a non-empty name is required, but the user hasn't entered a valid name.
   */
  analysisTableName?: string;
  /**
   * Studies to add the analysis to.
   *
   * No studies are required, and the list may be empty.
   */
  studyItems: Array<StudySearchItem>;
  /**
   * Defines how the analysis should seed itself with auto-generated tables.
   *
   * A missing value means seed data info is not required, and options to select seed data will not be shown.
   */
  seedDataConfig?: SeedDataConfig;
}

/**
 * How to default the create analysis form.
 */
interface CreateAnalysisStateDefaults {
  /**
   * Default name of the analysis.
   *
   * If this value is undefined, the analysis name field will default to empty.
   */
  initialAnalysisName?: string | (() => string);
  /**
   * If a single table will be added to the new analysis, the default name of the table.
   *
   * If this value is undefined, the table name field will not be shown.
   */
  initialAnalysisTableName?: string;
  /**
   * Default configuration for how the analysis should seed itself with auto-generated tables.
   *
   * If this value is undefined, the section to select seed data will not be shown.
   */
  initialSeedDataConfig?: SeedDataConfig;
  /**
   * Default studies to add the analysis to.
   *
   * If this value is undefined, no studies will be selected by default.
   */
  initialStudyItems?: Array<StudySearchItem>;
}

/**
 * Validated reflection of `CreateAnalysisState`, to use as request params for `AnalysisDAO.createAnalysis`.
 *
 * `analysisTableName` is returned separately for callers that add a table to the analysis in a separate
 * request, after the analysis has been created.
 */
export type CreateAnalysisRequestParamsInfo =
  | {isComplete: false}
  | {isComplete: true; requestParams: CreateAnalysisParams; analysisTableName: string | null};

/**
 * Hook to manage state for `CreateAnalysisForm`
 *
 * See `CreateAnalysisStateDefaults` for options to default the form state.
 *
 * @returns {createAnalysisState} State for `CreateAnalysisForm`
 * @returns {setCreateAnalysisState} Function to update `createAnalysisState`
 * @returns {requestParamsInfo} A transformed reflection of `CreateAnalysisForm` to use as request params.
 */
export default function useCreateAnalysisState({
  initialAnalysisName,
  initialAnalysisTableName,
  initialSeedDataConfig,
  initialStudyItems,
}: CreateAnalysisStateDefaults): {
  createAnalysisState: CreateAnalysisState;
  setCreateAnalysisState: (newState: CreateAnalysisState) => void;
  requestParamsInfo: CreateAnalysisRequestParamsInfo;
} {
  const defaultState: CreateAnalysisState = {
    analysisName: getInitialAnalysisName(initialAnalysisName),
    folderId: FolderDefaults.get(FolderDefaultsKey.ANALYSIS) ?? null,
    analysisTableName: initialAnalysisTableName,
    seedDataConfig: initialSeedDataConfig,
    studyItems: initialStudyItems ?? [],
  };
  const [createAnalysisState, setCreateAnalysisState] = useState<CreateAnalysisState>(defaultState);
  const requestParamsInfo = getCreateAnalysisRequestParamsInfo(createAnalysisState);

  return {createAnalysisState, setCreateAnalysisState, requestParamsInfo};
}

function getInitialAnalysisName(initialAnalysisName?: string | (() => string)): string {
  if (typeof initialAnalysisName === 'function') {
    return initialAnalysisName();
  }
  return initialAnalysisName ?? '';
}
